import i18next from "i18next";

import { formatDueDate } from "@/core/date";
import { defaultCurrencyFormat } from "@/core/number";
import { fakeI18n } from "@/i18n";
import {
    Contract,
    ContractCategoryType,
    ContractDetail,
    ContractPagination,
    IdNamePair,
    RelatedContract,
    RelatedContractPagination,
    RelatedContractType,
    SearchableContract,
} from "@/shared/domain";
import { sortCategories } from "@/shared/utils";

import { getSpApi, pagedRequestConfig, requestConfig } from "./apiRoot";
import { UrlManager } from "./UrlManager";

// Swagger: https://ibpcsrcdev01.portima.net/mybrokerapi/swagger/index.html

export const contractMapper = (source: MyBrokerApi.ContractV2): Contract => {
    return {
        ...source,
        id: source.id,
    };
};

export const searchableContractMapper = (source: Contract[]): SearchableContract[] => {
    return source?.map((contract) => ({
        ...contract,
        amountString: defaultCurrencyFormat(contract.amount, i18next.language),
        dueDateString: formatDueDate(contract.dueDate),
        referenceString: `Ref ${contract.reference}`,
    }));
};

export const contractDetailMapper = (source: MyBrokerApi.ContractDetail): Contract => {
    return {
        ...source,
        id: source.id,
        state: source.contractStatusCode,
        dueDate: source.dueDateDDMM
            ? source.dueDateDDMM.substring(2, 4) + source.dueDateDDMM.substring(0, 4)
            : undefined,
        amount: source.lastTotalAmount,
    };
};

export const relatedContractMapper = (source: MyBrokerApi.RelatedContract, policyholderId: number): RelatedContract => {
    const { contractBranch, contractRef, contractId, ...sourceRest } = source;
    return {
        ...sourceRest,
        id: parseInt(contractId, 10),
        reference: contractRef,
        policyholderId,
        state: { id: "1", name: fakeI18n.t("Common.Active") },
        branch: contractBranch as IdNamePair,
    };
};

/**
 * Returns the list of contract categories
 */
export const getContractCategories = async (policyholdersIdList = []): Promise<MyBrokerApi.CategoryCounters[]> => {
    const axiosRequestConfig = requestConfig<Paths.ApiContractCategoryCounters.Get.QueryParameters>({
        params: {
            policyholdersIdList,
        },
    });
    const { data } = await getSpApi().get<MyBrokerApi.CategoryCounters[]>(
        UrlManager.contractsCategoryCounters(),
        axiosRequestConfig,
    );

    return sortCategories(data);
};

/**
 * Returns a list of contracts
 */

export type ContractCategoryQueryParameters = Paths.ApiContract.Get.QueryParameters;

export const getRawContracts = async (
    requestParams: ContractCategoryQueryParameters = {},
): Promise<MyBrokerApi.ContractV2Payload> => {
    const axiosRequestConfig = pagedRequestConfig<ContractCategoryQueryParameters>({ params: requestParams });
    const { data } = await getSpApi().get<MyBrokerApi.ContractV2Payload>(UrlManager.contracts(), axiosRequestConfig);
    return data;
};

const mapContracts = (data: MyBrokerApi.ContractV2Payload): ContractPagination => {
    return { ...data, result: data.result.map((item) => contractMapper(item)).unique("id") } as ContractPagination;
};

export const getContracts = async (
    requestParams: ContractCategoryQueryParameters = {},
    categoryId: string = ContractCategoryType.ALL,
): Promise<ContractPagination> => {
    requestParams.categoryCode = +categoryId;
    const data = await getRawContracts(requestParams);
    return mapContracts(data);
};

export const getContractsLazy = async (
    requestParams: ContractCategoryQueryParameters = {},
): Promise<ContractPagination> => {
    const axiosRequestConfig = pagedRequestConfig<ContractCategoryQueryParameters>({ params: requestParams });

    const { data } = await getSpApi().get<MyBrokerApi.ContractV2Payload>(UrlManager.contracts(), axiosRequestConfig);
    return mapContracts(data);
};

/**
 * Returns contracts quantity
 */
export const getContractsCount = async (policyholdersIdList = [], categoryCode = "0"): Promise<number> => {
    const axiosRequestConfig = requestConfig<Paths.ApiContractCount.Get.QueryParameters>({
        params: {
            policyholdersIdList,
            categoryCode: +categoryCode,
        },
    });
    const { data } = await getSpApi().get<number>(UrlManager.contractsCount(), axiosRequestConfig);
    return data;
};

/**
 * Returns a list of insured good contracts
 */
type RelatedContractQueryParameters = Paths.ApiContractInsuredgood$Id.Get.QueryParameters;

export const getRelatedContracts = async (
    id: string | number,
    featureType: RelatedContractType,
    ibpAclId: number,
    policyholderId: number,
    requestParams: RelatedContractQueryParameters = {},
): Promise<RelatedContractPagination> => {
    const axiosRequestConfig = pagedRequestConfig<RelatedContractQueryParameters>({
        params: { ...requestParams, ibpAclId },
    });

    const { data } = await getSpApi().get<MyBrokerApi.RelatedContractPayload>(
        UrlManager.relatedContracts(id, featureType),
        axiosRequestConfig,
    );

    return {
        ...data,
        result: data.result.map((item) => relatedContractMapper(item, policyholderId)).unique("id"),
    } as RelatedContractPagination;
};

export const getRelatedContractsLazy = async (
    id: string | number,
    featureType: RelatedContractType,
    ibpAclId: number,
    policyholderId: number,
    pageNumber = 1,
): Promise<RelatedContractPagination> => {
    const axiosRequestConfig = pagedRequestConfig<RelatedContractQueryParameters>({ params: { pageNumber, ibpAclId } });

    const { data } = await getSpApi().get<MyBrokerApi.RelatedContractPayload>(
        UrlManager.relatedContracts(id, featureType),
        axiosRequestConfig,
    );

    return {
        ...data,
        result: data.result.map((item) => relatedContractMapper(item, policyholderId)).unique("id"),
    } as RelatedContractPagination;
};

export const getRawContract = async (
    contractId: number,
    policyHolderId: number,
    categoryCode?: string,
    stateCode?: string,
): Promise<MyBrokerApi.ContractDetail> => {
    const axiosRequestConfig = requestConfig<Paths.ApiContract$Id.Get.QueryParameters>({
        params: { policyHolderId, categoryCode, stateCode },
    });
    const { data } = await getSpApi().get<MyBrokerApi.ContractDetail>(
        UrlManager.contract(contractId),
        axiosRequestConfig,
    );
    return data;
};

//  Returns single contract

export const getContract = async (
    contractId: number,
    policyHolderId: number,
    categoryCode?: string,
    stateCode?: string,
): Promise<ContractDetail> => {
    const data = await getRawContract(contractId, policyHolderId, categoryCode, stateCode);
    return contractDetailMapper(data);
};

export const getContractSchool = async (policyholderIds: number[]): Promise<Contract[]> => {
    const url = `${UrlManager.contractsSchool()}?pageNumber=1&pageSize=50&policyholdersIdList=${policyholderIds.join(
        ",",
    )}`;
    const { data } = await getSpApi().get<MyBrokerApi.ContractV2Payload>(url);
    const { result } = data;
    return result.map((item) => contractMapper(item)).unique("id");
};

export const getContractsForLiability = async (
    policyholderId: number,
    accessRightsId: number,
): Promise<MyBrokerApi.TenYearsLiabilityContract[]> => {
    const axiosRequestConfig = requestConfig<Paths.ApiContractTenyearsliability$PolicyHolderId.Get.QueryParameters>({
        params: { accessRightsId },
    });

    const { data } = await getSpApi().get<MyBrokerApi.TenYearsLiabilityContract[]>(
        UrlManager.contractsLiabilityUrl(policyholderId),
        axiosRequestConfig,
    );
    return data;
};
